import { IOperation } from 'Models/Operation';
import React, { useEffect, useState } from 'react';
import { usePutOperationMutation } from 'redux/api/operationApi';
import { useSelector, useDispatch } from "react-redux";
import { setCurrentOperation } from "redux/features/operationSlice";
import { Button, Typography, Card, TextField, Select, MenuItem, FormControl, InputLabel, FormHelperText, CardContent, CardHeader, Grid, Autocomplete } from '@mui/material';
import FullScreenLoader from 'components/FullScreenLoader';
import { validateIdentityDocument } from 'utils/validateNif';
import { useIntl } from 'react-intl';
import { useGetAllCountriesQuery } from 'redux/api/countryApi';
import { ICountry } from 'Models/Country';

const DocumentVerification: React.FC<{ nextStep: () => void, previousStep: () => void }> = ({ nextStep, previousStep }) => {
    const dispatch = useDispatch();
    const { formatMessage,locale } = useIntl();
    const [initialState, setInitialState] = useState<IOperation | null>(null);
    const [clientName, setClientName] = useState('');
    const [nationalityCode, setNationalityCode] = useState('');
    const [documentNumber, setDocumentNumber] = useState('')
    const [clientAddress, setClientAddress] = useState('')
    const [documentExpirationDate, setDocumentExpirationDate] = useState('')
    const [clientBirthDate, setClientBirthDate] = useState('')
    const [province, setProvince] = useState('')
    //const [locality, setLocality] = useState('')
    const [sex, setSex] = useState('')
    const [error, setError] = useState(false);
    const [dniError, setDniError] = useState(false);
    const [nameError, setNameError] = useState(false);
    const [expirationDateError, setExpirationDateError] = useState(false);
    const [birthDateError, setBirthDateError] = useState(false);

    const operation = useSelector((state: any) => state.operationState?.currentOperation?.data || state.operationState?.currentOperation);
    const documentTypeName = useSelector((state: any) => state.operationState?.documentTypeName);

    //api
    const [updateOperation, { data: postedOperationData, isSuccess: isPostedOperationSuccess, error: apiError }] = usePutOperationMutation();   
    const { data: allCountries, isLoading: isAllCountriesLoading, isError } = useGetAllCountriesQuery();
    
    const handleNationality = React.useCallback(() => {
        const country = allCountries?.find((c) => c.code === operation?.nationality) || null;
        if (country) {
            setNationalityCode(country.code);
        }
    }, [allCountries, operation?.nationality]);

    useEffect(() => {
        if (operation) {
            setClientName(operation.clientName || '');
            setDocumentNumber(operation.documentNumber || '');
            setClientAddress(operation.clientAddress || '');
            setDocumentExpirationDate(operation.documentExpirationDate || '');
            setClientBirthDate(operation.clientBirthDate || '');
            setSex(operation.sex || '');
            setProvince(operation.province || '');
            setInitialState({
                ...operation
            });
            handleNationality();
        }
    }, [operation, allCountries, handleNationality]);



    if (!operation || isAllCountriesLoading) return <FullScreenLoader />;

    function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
        event.preventDefault();
        if (clientName === '' || nationalityCode === '' || documentNumber === '' || documentExpirationDate === '' || clientBirthDate === '' || sex === ''
            || (documentTypeName === "DNI" && (province === '' || clientAddress === ''))
        ) {
            setError(true);
            return;
        }       
        if( !validateName(clientName)){
            setError(true);
            setNameError(true);            
            return;
        } else setNameError(false);
        setError(false);

        if (!validateData()) {
            return;
        }
        setError(false);
        const updatedOperation: IOperation = {
            ...operation,
            clientName: clientName || operation.clientName,
            nationality: nationalityCode || operation.nationality,
            documentNumber: documentNumber || operation.documentNumber,
            clientAddress: clientAddress || (operation.clientAddress ?? 'ND'),
            documentExpirationDate: documentExpirationDate || operation.documentExpirationDate,
            clientBirthDate: clientBirthDate || operation.clientBirthDate,
            sex: sex || operation.sex,
            province: province || (operation.province ?? 'ND'),
            mrzChecked: true,
        };

        if (JSON.stringify(updatedOperation) === JSON.stringify(initialState)) {//no hay cambios, salta llamada api
            nextStep();
            return;
        }

        const formData = new FormData();
        formData.append("data", JSON.stringify(updatedOperation));
        updateOperation({ id: operation.id, operationToUpdate: formData });
    }

    if (isPostedOperationSuccess && postedOperationData) {
        const operationData = "data" in postedOperationData
            ? postedOperationData.data // Si tiene `data`, accede a ella
            : postedOperationData;
        const updatedOperation = {
            ...operationData, // Datos del backend
            documentTypeName: operation.documentTypeName, // Preservar documentTypeName
        };

        dispatch(setCurrentOperation(updatedOperation));
        nextStep();
    }
    if (apiError && 'status' in apiError && apiError.status === 403) {
        const updatedOperation: IOperation = {
            ...operation,
            clientName: clientName || operation.data.clientName,
            nationality: nationalityCode || operation.data.nationality,
            documentNumber: documentNumber || operation.data.documentNumber,
            clientAddress: clientAddress || operation.data.clientAddress,
            documentExpirationDate: documentExpirationDate || operation.data.documentExpirationDate,
            clientBirthDate: clientBirthDate || operation.data.clientBirthDate,
            sex: sex || operation.data.sex,
            province: province || operation.data.province,
            verifiedOperation: true
        };
        const formData = new FormData();
        formData.append("data", JSON.stringify(updatedOperation));
        updateOperation({ id: operation.id, operationToUpdate: formData }).then((res) => {
            if ('data' in res) {
                dispatch(setCurrentOperation(res.data));
                nextStep();
            }
        });
        dispatch(setCurrentOperation(updatedOperation));
    }

    if (apiError) {
        console.log('Error:', apiError);
        return <div>{JSON.stringify(apiError)}</div>;
    }
    const formatDate = (date: string) => {
        // Comprobamos si la fecha ya está en el formato adecuado (yyyy-mm-dd)
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (regex.test(date)) {
            return date; // Si ya está en el formato adecuado, la devolvemos tal cual
        }

        // Si no está en el formato adecuado, la convertimos de dd/mm/aa a yyyy-mm-dd
        const [day, month, year] = date.split('/');
        return `20${year}-${month}-${day}`;
    }

    const formatBirthDate = (date: string): string | null => {
        // Comprobamos si la fecha ya está en el formato adecuado (yyyy-mm-dd)
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (regex.test(date)) {
            return date; // Si ya está en el formato adecuado, la devolvemos tal cual
        }
    
        // Si no está en el formato adecuado, asumimos que es dd/mm/aa
        const [day, month, year] = date.split('/');
        const currentYear = new Date().getFullYear();
        const fullYear = parseInt(year, 10) > currentYear % 100 ? `19${year}` : `20${year}`;
    
        return `${fullYear}-${month}-${day}`; // Devolvemos en formato yyyy-mm-dd
    };

    const validateName = (name: string): boolean => {
        if( !name.includes(',')){
            
            return false;
        }
        const [firstName, lastName] = name.split(',').map(s => s.trim());
        
        if (!firstName || !lastName) {
            setError(true);
            setNameError(true);
            return false;
        } else {
            setNameError(false);
        }
        return true;

    }
    const validateData = (): boolean => {
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // Formato yyyy-mm-dd
        const validSexValues = ['M', 'F']; // Valores válidos para sexo
        

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Normalizar la fecha a medianoche para evitar problemas con horas

        const formattedExpirationDate = formatDate(documentExpirationDate);
        const expirationDate = new Date(formattedExpirationDate);

        if (!dateRegex.test(formattedExpirationDate) || expirationDate < today) {
            setError(true);
            setExpirationDateError(true);
            return false;
        }else{
            setExpirationDateError(false);
        }

        const formattedBirthDate = formatBirthDate(clientBirthDate);
        if (!formattedBirthDate || !dateRegex.test(formattedBirthDate)) {
            setError(true);
            return false;
        }

        // Validación de rango de fechas
        const birthDate = new Date(formattedBirthDate);
        const currentYear = new Date().getFullYear();
        const birthYear = birthDate.getFullYear();

        if (birthYear < 1900 || birthYear > currentYear) {
            setBirthDateError(true);
            setError(true);
            return false;
        } else {
            setBirthDateError(false);
        }

        if (!validSexValues.includes(sex)) {
            setError(true);
            return false;
        }
        if (documentTypeName === "DNI" && !validateIdentityDocument(documentNumber)) {
            setError(true);
            setDniError(true);
            return false;
        } else setDniError(false);

        return true;
    }
    
    return (
        <Card style={{ margin: '0 auto', width: '100%', padding: '20px' }}>
            <CardHeader
                title={
                    <Typography variant="h1" align="center">
                        {formatMessage({ id: "document.verification.title" })}
                    </Typography>
                }
            />
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '10px' }}>
                        {!operation.documentNumber && <p style={{ color: 'red' }}>{formatMessage({ id: "document.verification.failed" })}</p>}

                        <TextField
                            fullWidth
                            id='clientName'
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            margin="normal"
                            variant="outlined"
                            label={formatMessage({ id: "document.verification.document.name" })}
                            helperText={nameError && formatMessage({ id: "document.verification.document.name.error" })}
                            error={nameError}
                        />

                        <TextField
                            fullWidth
                            id='documentNumber'
                            value={documentNumber}
                            onChange={(e) => setDocumentNumber(e.target.value)}
                            margin="normal"
                            variant="outlined"
                            label={formatMessage({ id: "document.verification.document.number" })}
                            error={dniError}
                            helperText={dniError && formatMessage({ id: "document.verification.document.number.error" })}
                        />

                        <Autocomplete
                            id="nationality"
                            fullWidth
                            options={allCountries || []}
                            getOptionLabel={(option: ICountry | string) => {
                                if (typeof option === 'string') return option;
                                return locale === 'es' ? option.nameEsp : option.nameEng;
                            }}
                            value={
                                allCountries?.find((c) => c.code === nationalityCode) || null
                            }
                            onChange={(event, newValue) => {                              
                                if (typeof newValue === 'string' || !newValue) {
                                  setNationalityCode('');
                                } else {
                                  setNationalityCode((prev) => {
                                    // Si es distinto, actualizamos
                                    if (prev !== newValue.code) return newValue.code;
                                    // Si es el mismo, forzamos re-render con string idéntico (evita quedarse vacío)
                                    return `${newValue.code}`;
                                  });
                                }
                              }}
                            isOptionEqualToValue={(option, value) => option.code === value.code}
                            renderInput={(params) => (
                                <TextField
                                {...params}
                                label={formatMessage({ id: "document.verification.nationality" })}
                                margin="normal"
                                variant="outlined"
                                />
                            )}
                        />

                        {documentTypeName === "DNI" &&
                            <>
                                <TextField
                                    fullWidth
                                    id='province'
                                    value={province}
                                    onChange={(e) => setProvince(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    label={formatMessage({ id: "document.verification.province" })}
                                />
                                <TextField
                                    fullWidth
                                    id='clientAddress'
                                    value={clientAddress}
                                    onChange={(e) => setClientAddress(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    label={formatMessage({ id: "document.verification.address" })}
                                />
                            </>
                        }
                        {documentTypeName === "UE ID Card" &&
                            <>
                                <TextField
                                    fullWidth
                                    id='clientAddress'
                                    value={clientAddress}
                                    onChange={(e) => setClientAddress(e.target.value)}
                                    margin="normal"
                                    variant="outlined"
                                    label={formatMessage({ id: "document.verification.address" })}
                                />
                            </>
                        }

                        <TextField
                            fullWidth
                            id='documentExpirationDate'
                            type="date"
                            value={formatDate(documentExpirationDate)}
                            onChange={(e) => setDocumentExpirationDate(e.target.value)}
                            margin="normal"
                            variant="outlined"
                            label={formatMessage({ id: "document.verification.expiry.date" })}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText={expirationDateError && formatMessage({ id: "document.verification.expiry.date.error" })}
                            error={expirationDateError}
                        />

                        <TextField
                            fullWidth
                            id='clientBirthDate'
                            type="date"
                            value={formatBirthDate(clientBirthDate)}
                            onChange={(e) => setClientBirthDate(e.target.value)}
                            margin="normal"
                            variant="outlined"
                            label={formatMessage({ id: "document.verification.birth.date" })}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            helperText={birthDateError && formatMessage({ id: "document.verification.birth.date.error" })}
                            error={birthDateError}
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel>{formatMessage({ id: "document.verification.sex" })}</InputLabel>
                            <Select
                                value={sex}
                                onChange={(e) => setSex(e.target.value)}
                                label="Sexo"
                            >
                                <MenuItem value="M">{formatMessage({ id: "document.verification.male" })}</MenuItem>
                                <MenuItem value="F">{formatMessage({ id: "document.verification.female" })}</MenuItem>
                            </Select>
                            {error && <FormHelperText error>{formatMessage({ id: "document.verification.help" })}</FormHelperText>}
                        </FormControl>
                    </div>

                    <Grid container spacing={2} justifyContent="space-between" >
                        <Grid item xs={12} lg={6}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                fullWidth
                                onClick={previousStep}
                                sx={{
                                    mt: 2,
                                    backgroundColor: "#FFF",
                                    display: {
                                        xs: "none", // Ocultar en pantallas pequeñas (móviles)
                                        sm: "none", // Ocultar en tablets
                                        md: "block", // Mostrar en pantallas medianas y grandes
                                    },
                                }}
                            >
                                {formatMessage({ id: "buttons.back" })}
                            </Button>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <Button variant="contained" color="secondary" type="submit" fullWidth sx={{ mt: 2 }}>
                                <Typography fontWeight={500} fontSize={"16px"} color="#FFF" alignContent="center">
                                    {formatMessage({ id: "buttons.confirm" })}
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </CardContent>
        </Card>
    );
};

export default DocumentVerification;
